import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuthSlice } from '../../features/auth/authSlice';
import { useGuildsSlice } from '../../features/guilds/guildsSlice';
import { useUISlice } from '../../features/ui/uiSlice';
import {
  useGetWatchedGuildsQuery,
  useRemoveWatchedGuildMutation,
} from '../../features/guilds/guildsApi';
import { ErrorMessage } from '../common/ErrorMessage';
import { SkeletonGuildGrid } from '../common/Skeleton';
import { GuildCard } from './GuildCard';
import { AddServerModal } from './AddServerModal';
import type { Guild, WatchedGuild } from '../../types';

export const GuildSelector: React.FC = () => {
  const authSlice = useAuthSlice();
  const guildsSlice = useGuildsSlice();
  const uiSlice = useUISlice();

  const [isAddOpen, setIsAddOpen] = useState(false);

  const {
    data: watchedGuilds = [],
    isLoading,
    isFetching,
    error,
  } = useGetWatchedGuildsQuery(undefined, {
    skip: !authSlice.auth?.access_token,
  });

  const [removeWatchedGuild] = useRemoveWatchedGuildMutation();

  const handleSelectGuild = useCallback(
    (guildId: string) => {
      guildsSlice.setSelectedGuild(guildId);
      uiSlice.setView('templates');
    },
    [guildsSlice, uiSlice]
  );

  const handleRemoveGuild = useCallback(
    async (guildId: string) => {
      try {
        await removeWatchedGuild({ guildId }).unwrap();
        toast.success('Server removed from tracking.');
      } catch {
        toast.error('Failed to remove server.');
      }
    },
    [removeWatchedGuild]
  );

  // Wait for auth + the first fetch to settle before deciding loading vs error vs empty
  if (!authSlice.auth?.access_token || isLoading || isFetching) {
    return <SkeletonGuildGrid />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load your tracked servers. Please try again." />;
  }

  // Empty state — no watched guilds yet
  if (watchedGuilds.length === 0) {
    return (
      <>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-surface-container flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-4xl text-on-surface-variant">
                dns
              </span>
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-on-surface mb-2">
              Track your first server
            </h2>
            <p className="text-on-surface-variant text-sm max-w-md mb-8">
              Add servers you administer to manage their role templates from here.
              You won't see other servers — just the ones you choose.
            </p>
            <button
              onClick={() => setIsAddOpen(true)}
              className="gradient-primary text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-xl">add</span>
              Add a Server
            </button>
          </div>
        </div>
        {isAddOpen && <AddServerModal onClose={() => setIsAddOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 px-1 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-on-surface mb-2">
              Your Tracked Servers
            </h1>
            <p className="text-on-surface-variant text-sm">
              Select a server to manage its role templates.
            </p>
          </div>
          <button
            onClick={() => setIsAddOpen(true)}
            className="gradient-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center gap-1.5 flex-shrink-0"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Add Server
          </button>
        </div>

        {/* Guild Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {watchedGuilds.map((watched: WatchedGuild) => {
            const guild: Guild = {
              id: watched.guild_id,
              name: watched.name,
              icon: watched.icon,
              owner: false,
              permissions: '0',
              features: [],
            };

            return (
              <GuildCard
                key={guild.id}
                guild={guild}
                isAvailable={true}
                isBotPresent={true}
                isUserAdmin={true}
                onSelect={handleSelectGuild}
                onRemove={handleRemoveGuild}
              />
            );
          })}

          {/* Add server CTA */}
          <button
            onClick={() => setIsAddOpen(true)}
            className="border-2 border-dashed border-outline-variant/30 rounded-lg flex flex-col items-center justify-center p-6 text-center hover:bg-surface-container-low transition-colors group"
          >
            <span className="material-symbols-outlined text-3xl text-outline-variant group-hover:text-primary-text transition-colors mb-2">
              add_circle
            </span>
            <p className="text-sm font-semibold text-on-surface">Add Server</p>
            <p className="text-[11px] text-on-surface-variant">Track another guild</p>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-outline-variant/15 flex items-center justify-end px-1">
          <span className="text-[11px] text-on-surface-variant">
            {watchedGuilds.length} tracked {watchedGuilds.length === 1 ? 'server' : 'servers'}
          </span>
        </div>
      </div>

      {isAddOpen && <AddServerModal onClose={() => setIsAddOpen(false)} />}
    </>
  );
};
